import { format } from "date-fns";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice.ts";
import { imgAvatar } from "@/shared/assets/img";
import { BucketIcon, Building2Icon, CalendarIcon, RubleIcon, TimeIcon } from "@/shared/icons";
import { Button, ProgressRadial } from "@/shared/ui";
import { AccountState } from "@/domains/account/slice";
import { formatMinutes } from "@/domains/account/helpers/formatMinutes";

type Props = {
  character: AccountState["characters"][0];
  onClick?: () => void;
  onRestore?: () => void;
  pending?: boolean;
};

export default function AccountChar({ character, onClick, onRestore, pending }: Props) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="h-full flex flex-col grow rounded-[inherit]" onClick={onClick}>
      <ProgressRadial percent={character.level} className="absolute -top-6 right-6 z-10" />
      <div className="flex items-center gap-2.5 mb-1">
        <div className="rounded-xl w-11 aspect-square relative p-1">
          <div className="rounded-border" />
          <img src={imgAvatar} alt="avatar" className="w-full h-full object-cover rounded" />
        </div>
        <div>
          <div className="uppercase font-bold text-sm">
            {character.firstName} {character.lastName}
          </div>
          <div className="text-sm text-accent">
            <span className="font-bold">ID:</span> #{character.id}
          </div>
        </div>
      </div>

      {character.isDeleted ? (
        <div className="grow flex flex-col gap-2">
          <div
            className={clsx(
              "flex items-center justify-center",
              "w-full bg-gradient-to-r from-[#FF7958]/15 to-[#FF7958]/[.07] text-[#FF9E58]",
              "text-14 font-medium py-2.5 rounded-[0.5rem] mt-auto text-center px-3",
            )}
          >
            Персонаж будет удален {format(new Date(character.completeDeletedAt), "dd.MM.yyyy")}
          </div>
          <Button
            type="button"
            variant="soft"
            color="gray"
            className="w-full h-[2.75rem]"
            onClick={onRestore}
            loading={pending}
            disabled={pending}
          >
            <BucketIcon size={18} className="rm-2.5" />
            Отменить удаление
          </Button>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="flex items-center gap-3.5 py-3.5 relative">
            <Building2Icon size={20} />
            <div className="text-white/70 mr-auto">Организация:</div>
            <div className="font-bold">Нет</div>
            <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-inverted to-inverted/50 opacity-6" />
          </div>
          <div className="flex items-center gap-3.5 py-3.5 relative">
            <RubleIcon size={20} />
            <div className="text-white/70 mr-auto">Всего денег:</div>
            <div className="font-bold">{formatPrice(character.balance)}</div>
            <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-inverted to-inverted/50 opacity-6" />
          </div>
          <div className="flex items-center gap-3.5 py-3.5 relative">
            <TimeIcon size={20} />
            <div className="text-white/70 mr-auto">Всего в игре:</div>
            <div className="font-bold">{formatMinutes(character.totalPlayingTimeMinutes)}</div>
            <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-inverted to-inverted/50 opacity-6" />
          </div>
          <div className="flex items-center gap-3.5 py-3.5 relative">
            <CalendarIcon size={20} />
            <div className="text-white/70 mr-auto">Последний вход:</div>
            <div className="font-bold">
              {character.lastJoinAt
                ? format(new Date(character.lastJoinAt), "dd.MM.yyyy HH:mm")
                : "-"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
